/**
 * Seeded xorshift128 — the only source of randomness the simulation may use.
 *
 * `Math.random()` is banned under `src/sim/` by lint. This is its replacement:
 * pure integer state, serializable, and forkable so subsystems get independent
 * streams without coupling their draw counts to each other.
 */

import type { Fixed } from './fixed.js'

const INV_U32 = 1 / 4294967296

export class Rng {
  private s0 = 0
  private s1 = 0
  private s2 = 0
  private s3 = 0
  /** Fork counter. Part of the state, but never consumed by `next()`. */
  private forkSeq = 0

  constructor(seed: number | string = 0) {
    this.seed(seed)
  }

  /** Re-seed from a number or a string. Any seed produces a usable state. */
  seed(seed: number | string): void {
    let h = typeof seed === 'string' ? hashString(seed) : seed | 0
    // SplitMix-style scrambling so adjacent numeric seeds produce unrelated
    // streams — mapgen relies on this for "adjacent seeds differ".
    this.s0 = (h = mix32(h + 0x9e3779b9)) | 0
    this.s1 = (h = mix32(h + 0x9e3779b9)) | 0
    this.s2 = (h = mix32(h + 0x9e3779b9)) | 0
    this.s3 = mix32(h + 0x9e3779b9) | 0
    if ((this.s0 | this.s1 | this.s2 | this.s3) === 0) this.s0 = 1
    this.forkSeq = 0
  }

  /** Next raw 32-bit value, as an unsigned integer. */
  next(): number {
    let t = this.s3
    const s = this.s0
    this.s3 = this.s2
    this.s2 = this.s1
    this.s1 = s
    t ^= t << 11
    t ^= t >>> 8
    this.s0 = (t ^ s ^ (s >>> 19)) | 0
    return this.s0 >>> 0
  }

  /** Uniform integer in `[0, n)`. Rejection-sampled, so it is unbiased. */
  int(n: number): number {
    if (n <= 1) return 0
    const limit = 4294967296 - (4294967296 % n)
    let v = this.next()
    while (v >= limit) v = this.next()
    return v % n
  }

  /** Uniform integer in `[lo, hi]`, inclusive. */
  range(lo: number, hi: number): number {
    return lo + this.int(hi - lo + 1)
  }

  /** Uniform Q16.16 value in `[0, 1)`. */
  fixed(): Fixed {
    return (this.next() >>> 16) as Fixed
  }

  /** Uniform Q16.16 value in `[lo, hi)`. */
  fixedRange(lo: Fixed, hi: Fixed): Fixed {
    const span = hi - lo
    return (lo + ((span * (this.next() >>> 16)) >> 16)) as Fixed
  }

  /** True with probability `numerator / denominator`. */
  chance(numerator: number, denominator: number): boolean {
    return this.int(denominator) < numerator
  }

  /** Render-side only. Never call this from `src/sim/`. */
  float(): number {
    return this.next() * INV_U32
  }

  /**
   * A new independent stream derived from the current state.
   *
   * Forking does **not** advance the parent stream — the parent's sequence of
   * `next()` values is identical whether or not a fork happened. Only the fork
   * counter moves, so two forks taken at the same point still differ.
   */
  fork(): Rng {
    const child = new Rng(0)
    const seq = this.forkSeq++
    child.s0 = mix32(this.s0 ^ mix32(seq + 0x85ebca6b)) | 0
    child.s1 = mix32(this.s1 ^ mix32(seq + 0xc2b2ae35)) | 0
    child.s2 = mix32(this.s2 ^ mix32(seq + 0x27d4eb2f)) | 0
    child.s3 = mix32(this.s3 ^ mix32(seq + 0x165667b1)) | 0
    if ((child.s0 | child.s1 | child.s2 | child.s3) === 0) child.s0 = 1
    child.forkSeq = 0
    return child
  }

  /** Snapshot the full state, including the fork counter. */
  save(out?: Int32Array): Int32Array {
    const a = out ?? new Int32Array(5)
    a[0] = this.s0
    a[1] = this.s1
    a[2] = this.s2
    a[3] = this.s3
    a[4] = this.forkSeq
    return a
  }

  /** Restore a snapshot. The resumed sequence is identical to the original. */
  load(a: Int32Array): void {
    this.s0 = a[0] | 0
    this.s1 = a[1] | 0
    this.s2 = a[2] | 0
    this.s3 = a[3] | 0
    this.forkSeq = a[4] | 0
  }

  /**
   * A live view of the state for `hash.register`. The returned array is the
   * canonical storage — `next()` writes through to it — so registering it once
   * at init is enough.
   */
  hashView(): Int32Array {
    // The state lives in fields for speed, so the view is refreshed on read.
    // hash.register accepts a provider for exactly this case.
    return this.save(this.view)
  }

  private readonly view = new Int32Array(5)
}

function mix32(x: number): number {
  x = Math.imul(x ^ (x >>> 16), 0x85ebca6b)
  x = Math.imul(x ^ (x >>> 13), 0xc2b2ae35)
  return (x ^ (x >>> 16)) >>> 0
}

function hashString(s: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return h | 0
}
